import { getDay, isSameDay, set } from 'date-fns';
import { ReservationType, TimeSlot } from '../../types/reservationType';

export const FALLBACK_DEFAULT_DURATION = 60;

export interface InitialHours {
  start: string;
  end: string;
}

export interface CalendarRestrictions {
  minTime: Date;
  maxTime: Date;
  closedDays: number[];
  closedDates: string[];
  restHour?: TimeSlot;
}

const parseTimeString = (timeStr: string | undefined, isEndTime: boolean = false): Date | undefined => {
  if (!timeStr) return undefined;

  // Para horarios de cierre 00:00, devolvemos undefined para indicar que no hay límite
  if (isEndTime && timeStr === '00:00') {
    return undefined;
  }

  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date(2000, 0, 1); // Usar una fecha fija
  return set(date, { hours, minutes, seconds: 0, milliseconds: 0 });
};

const getWeekHours = (type: ReservationType): (TimeSlot | undefined)[] => {
  const hours = type.hours;
  return [hours.monday, hours.tuesday, hours.wednesday, hours.thursday, hours.friday, hours.saturday, hours.sunday];
};

export const getCommonRestrictions = (
  reservationTypes: ReservationType[] | undefined,
  selectedBranchId: string | null,
  selectedReservationType: ReservationType | null,
): CalendarRestrictions => {
  if (!reservationTypes || !reservationTypes.length) {
    const defaultDate = new Date();
    return {
      minTime: set(defaultDate, { hours: 7, minutes: 0, seconds: 0 }),
      maxTime: set(defaultDate, { hours: 23, minutes: 59, seconds: 59 }),
      closedDays: [],
      closedDates: [],
    };
  }

  // Filtrar tipos de reserva según los filtros aplicados
  let filteredTypes = [...reservationTypes];
  if (selectedBranchId) {
    filteredTypes = filteredTypes.filter((type) => type.branchId === selectedBranchId);
  }
  if (selectedReservationType) {
    filteredTypes = filteredTypes.filter((type) => type.name === selectedReservationType.name);
  }

  // Obtener horas de apertura y cierre
  const openingHours = filteredTypes
    .map((type) => {
      const weekHours = getWeekHours(type);
      return weekHours.filter(Boolean).map((slot) => slot?.start);
    })
    .flat()
    .filter(Boolean)
    .map((timeStr) => parseTimeString(timeStr, false))
    .filter(Boolean) as Date[];

  const closingHours = filteredTypes
    .map((type) => {
      const weekHours = getWeekHours(type);
      return weekHours.filter(Boolean).map((slot) => slot?.end);
    })
    .flat()
    .filter(Boolean)
    .map((timeStr) => parseTimeString(timeStr, true))
    .filter(Boolean) as Date[];

  // Encontrar la hora de apertura más temprana
  // With no opening hours left (for example a filter that leaves no type), Math.min() is Infinity: use the default 07:00
  const minTime =
    openingHours.length === 0
      ? set(new Date(), { hours: 7, minutes: 0, seconds: 0 })
      : new Date(Math.min(...openingHours.map((d) => d.getTime())));

  // Para la hora de cierre, verificar si hay algún horario 00:00
  const has00Closing = filteredTypes.some((type) => {
    const weekHours = getWeekHours(type);
    return weekHours.some((slot) => slot?.end === '00:00');
  });

  // Si hay algún horario de cierre a las 00:00, no establecer restricción de hora máxima
  let maxTime;
  if (has00Closing || closingHours.length === 0) {
    // Usando null no funcionará bien con react-big-calendar, así que usamos una hora tarde
    const defaultDate = new Date();
    maxTime = set(defaultDate, { hours: 23, minutes: 59, seconds: 59 });
  } else {
    maxTime = new Date(Math.max(...closingHours.map((d) => d.getTime())));
  }

  // Encontrar días cerrados comunes
  const closedDays = Array.from({ length: 7 }, (_, i) => i).filter((day) => {
    // Un día está cerrado si:
    // 1. Está marcado como cerrado en closedDays
    // 2. O no tiene horas definidas para ese día
    return filteredTypes.every((type) => {
      const daysClosed = type.closedDays?.map((d) => Number(d)) || [];
      const dayHours = (() => {
        switch (day) {
          case 0:
            return type.hours.sunday;
          case 1:
            return type.hours.monday;
          case 2:
            return type.hours.tuesday;
          case 3:
            return type.hours.wednesday;
          case 4:
            return type.hours.thursday;
          case 5:
            return type.hours.friday;
          case 6:
            return type.hours.saturday;
        }
      })();

      return daysClosed.includes(day) || !dayHours;
    });
  });

  // Verificar si todos los tipos tienen el mismo horario de descanso
  const restHours = filteredTypes.map((type) => type.hours.restTime).filter(Boolean);
  const restHour =
    restHours.length === filteredTypes.length &&
    restHours.every((rest) => rest?.start === restHours[0]?.start && rest?.end === restHours[0]?.end)
      ? restHours[0]
      : undefined;

  // Encontrar fechas específicas cerradas (usando closedDays temporalmente hasta que tengamos closedDates)
  // Start from the first type's dates, not from an empty list: an empty result means no common date, not "nothing yet" (#89)
  const [firstTypeDates = [], ...otherTypesDates] = filteredTypes.map((type) => type.closedDays ?? []);
  const closedDates = otherTypesDates.reduce(
    (acc: string[], dates) => acc.filter((date) => dates.includes(date)),
    firstTypeDates,
  );

  return {
    minTime,
    maxTime,
    closedDays,
    closedDates,
    restHour,
  };
};

export const adjustEndTimeToMaxLimit = (
  start: Date,
  maxTime: Date,
  durationMinutes: number = FALLBACK_DEFAULT_DURATION,
): Date => {
  // Crear una copia de la fecha de inicio
  const end = new Date(start);

  // Añadir la duración deseada en minutos
  end.setMinutes(end.getMinutes() + durationMinutes);

  // Crear una fecha con el mismo día que start pero con la hora de maxTime
  const maxTimeForDay = new Date(start);
  maxTimeForDay.setHours(maxTime.getHours(), maxTime.getMinutes(), 0, 0);

  // Si el tiempo final excede el límite máximo, ajustarlo al límite máximo
  if (end > maxTimeForDay) {
    return maxTimeForDay;
  }

  return end;
};

export const isInRestHour = (date: Date, restHour: TimeSlot | undefined): boolean => {
  if (!restHour) return false;

  const [restStartHour, restStartMinute] = restHour.start.split(':').map(Number);
  const [restEndHour, restEndMinute] = restHour.end.split(':').map(Number);
  const slotHour = date.getHours();
  const slotMinute = date.getMinutes();

  return (
    (slotHour > restStartHour || (slotHour === restStartHour && slotMinute >= restStartMinute)) &&
    (slotHour < restEndHour || (slotHour === restEndHour && slotMinute < restEndMinute))
  );
};

export const isClosedDay = (date: Date, restrictions: Pick<CalendarRestrictions, 'closedDays' | 'closedDates'>): boolean => {
  const day = getDay(date);
  const isClosedWeekday = restrictions.closedDays.includes(day);
  const isClosedDate = restrictions.closedDates.some((closedDate) => {
    return isSameDay(date, closedDate);
  });

  return isClosedWeekday || isClosedDate;
};
